/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { TEXT_CONSTANTS } from '@support/shared/constants/device.constants';

@Component({
  selector: 'tmo-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: [
    '../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../style-sheets/components/input-radio.component.scss',
    '../../../../../style-sheets/components/gallery.component.scss',
  ],
})
export class GalleryComponent implements OnInit, OnDestroy {
  /** Input variable to store Images or Videos array */
  @Input() public itemArray: Object[];
  /** Input variable to store whether it is being called for videos or not */
  @Input() public isVideo: boolean;
  /** Input variable to store the id for thumbnail part */
  @Input() public dynamicId: string;
  /** Variable to store video player */
  @ViewChild('videoPlayer', { static: false }) public videoplayer: ElementRef;
  /** Input variable to store page title to be displayed in Html */
  @Input() public pageTitle: string;

  /** Varibale to track if video is paused or not */
  public isVideoPaused: boolean;
  /** Varibale to track current strip position */
  public stripPosition: number;
  /** Varibale to store strip size */
  public stripSize: number;
  /** Varibale to scroll size */
  public scrollSize: number;
  /** Varibale to store number of gallery data */
  public galleryData: number;
  /** Varibale to track if left carrot need to be active */
  public leftActive: boolean;
  /** Varibale to track if right carrot need to be active */
  public rightActive: boolean;
  /** Varibale to track number of thumbnails */
  public thumbTotal: number;
  /** Varibale to track which image is being displayed in main gallery */
  public activeImage: Number;
  /** Varibale to track which thumbnail has been selected */
  public imagethumbSelected: Number;
  /** Varibale to store images coming from Image gallery */
  public imagesData: Object[];
  /** Varibale to store videos coming from Video gallery */
  public videosData: Object[];
  /** Varibale to track which video is being played in main gallery */
  public activeVideo: Number;
  /** Varibale to track which thumbnail has been selected */
  public videothumbSelected: Number;
  /** Varibale to track left most index of currently displayed strip */
  public leftActiveIndex: number;
  /** Varibale to track right most index of currently displayed strip */
  public rightActiveIndex: number;
  /** Variable to store the constant */
  public nextButton: string;
  /** Variable to store the constant */
  public previousButton: string;
  /** Variable to store the constant */
  public image: string;
  /** Variable to store the constant */
  public constantOf: string;

  constructor() {
    this.isVideoPaused = true;
    this.stripPosition = 0;
    this.stripSize = 0;
    this.scrollSize = 0;
    const galleryData = 6;
    this.galleryData = galleryData;
    this.leftActive = false;
    this.rightActive = false;
    this.thumbTotal = 0;
    this.activeImage = 0;
    const imagethumbSelected = 1;
    this.imagethumbSelected = imagethumbSelected;
    this.imagesData = [];
    this.videosData = [];
    this.activeVideo = 0;
    const videothumbSelected = 1;
    this.videothumbSelected = videothumbSelected;
    this.leftActiveIndex = 0;
    this.nextButton = TEXT_CONSTANTS.NEXTBUTTON;
    this.previousButton = TEXT_CONSTANTS.PREVIOUSBUTTON;
    this.image = TEXT_CONSTANTS.IMAGE;
    this.constantOf = TEXT_CONSTANTS.OF;
  }

  /**
   * Method to check whether to make left and right arrow (Carrot) active or not
   * @returns void
   */
  public checkArrows(): void {
    this.leftActive = this.leftActiveIndex > 0;
    this.rightActive = this.rightActiveIndex < this.itemArray.length - 1;
  }

  /**
   * Method to move strip by one item
   * @returns void
   */
  public moveStrip(): void {
    // @TODO:Make directive and remove document function
    const doc = document;
    const thumbsStrip = <HTMLDivElement>doc.getElementById(this.dynamicId);
    const newLeft = 0 + this.stripPosition * this.scrollSize;
    if (thumbsStrip) {
      thumbsStrip.style.left = newLeft.toString().concat('px');
      this.checkArrows();
    }
  }

  /**
   * Method to move strip by one item in right
   * @returns void
   */
  public moveRight(): void {
    if (this.rightActive) {
      this.leftActiveIndex++;
      this.rightActiveIndex++;
      this.stripPosition = this.stripPosition - 1;
      this.moveStrip();
    }
  }

  /**
   * Method to move strip by one item in left
   * @returns void
   */
  public moveLeft(): void {
    if (this.leftActive) {
      this.leftActiveIndex--;
      this.rightActiveIndex--;
      this.stripPosition = this.stripPosition + 1;
      this.moveStrip();
    }
  }

  /**
   * Method to set initial values and positions for the gallery components
   * @returns void
   */
  public setVars(): void {
    if (TEXT_CONSTANTS.winSize >= TEXT_CONSTANTS.BREAK_POINT_DESKTOP) {
      this.stripSize = TEXT_CONSTANTS.stripSizeForLargeScreen;
      this.scrollSize = TEXT_CONSTANTS.scrollSizeForDesktop;
      this.rightActiveIndex = TEXT_CONSTANTS.stripSizeForLargeScreen - 1;
    } else if (TEXT_CONSTANTS.winSize >= TEXT_CONSTANTS.BREAK_POINT_TABLET) {
      this.stripSize = TEXT_CONSTANTS.stripSizeForLargeScreen;
      this.scrollSize = TEXT_CONSTANTS.scrollSizeForTab;
      this.rightActiveIndex = TEXT_CONSTANTS.stripSizeForLargeScreen - 1;
    } else {
      this.stripSize = TEXT_CONSTANTS.stripSizeForSmallScreen;
      this.scrollSize = TEXT_CONSTANTS.scrollSizeForMobile;
      this.rightActiveIndex = TEXT_CONSTANTS.stripSizeForSmallScreen - 1;
    }
    this.moveStrip();
    this.checkArrows();
    this.thumbTotal = this.galleryData;
  }

  /**
   * Method to show video on click of its thumbnail
   * @param index : Holds the position of video that needs to be shown
   * @returns void
   */
  public showVideo(index: Number): void {
    this.activeVideo = index;
    this.videothumbSelected = +index + 1;
  }

  /**
   * Method to show image on click of its thumbnail
   * @param index : Holds the position of image that needs to be shown
   * @returns void
   */
  public showImage(index: Number): void {
    this.activeImage = index;
    this.imagethumbSelected = +index + 1;
  }

  /**
   * Component lifecycle hook to initialize the component
   * @returns void
   */
  public ngOnInit(): void {
    this.setVars();
  }

  /**
   * Component lifecycle hook to destroy the component
   * @returns void
   */
  public ngOnDestroy(): void {}

  /**
   * Method to play video in the video player
   * @returns void
   */
  public playVideo(): void {
    this.videoplayer.nativeElement.play();
    this.isVideoPaused = false;
  }

  /**
   * Method to pause video in the video player
   * @returns void
   */
  public pauseVideo(): void {
    this.videoplayer.nativeElement.pause();
    this.isVideoPaused = true;
  }
}
